import { useEffect, useRef, useState } from 'react';
import firebase from '../../auth/firebase';
import { getEmailSignInCodeError, requestEmailSignInCode, verifyEmailSignInCode } from '../../auth/emailCodeSignIn';
import { clearPendingEmailSignIn, getEmailSignInGeneration, readPendingEmailSignIn, savePendingEmailSignIn } from '../../auth/pendingEmailSignIn';

const currentUid = () => firebase.auth()?.currentUser?.uid || null;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function useEmailCodeSignIn(auth, language) {
  const [challenge, setChallenge] = useState(readPendingEmailSignIn);
  const [email, setEmail] = useState(() => readPendingEmailSignIn()?.email || localStorage.getItem('emailForSignIn') || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now);
  const [retryAt, setRetryAt] = useState(0);
  const operation = useRef(0);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    // Cleanup invalidates the current request generation, not a captured value.
    return () => { mounted.current = false; ++operation.current; };
  }, []);
  useEffect(() => {
    if (!challenge && !retryAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [challenge, retryAt]);
  const resendIn = Math.max(0, Math.ceil((Math.max(Date.parse(challenge?.resendAt) || 0, retryAt) - now) / 1000));
  const expiresIn = challenge ? Math.max(0, Math.ceil((Date.parse(challenge.expiresAt) - now) / 1000)) : 0;

  const send = async (event) => {
    event?.preventDefault();
    if (pending.current || resendIn > 0) return;
    const normalized = email.trim().toLowerCase();
    if (!emailPattern.test(normalized)) { setError('invalid_email'); return; }
    const id = ++operation.current;
    const uid = currentUid();
    const generation = getEmailSignInGeneration();
    const isCurrent = () => mounted.current && operation.current === id && currentUid() === uid && getEmailSignInGeneration() === generation;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const metadata = await requestEmailSignInCode(normalized, language);
      if (!isCurrent()) return;
      const next = { ...metadata, email: normalized };
      savePendingEmailSignIn(next);
      setChallenge(next);
      setEmail(normalized);
      setCode('');
      setRetryAt(0);
      setNow(Date.now());
    } catch (failure) {
      if (!isCurrent()) return;
      setError(getEmailSignInCodeError(failure));
      if (failure.retryAt) { setRetryAt(failure.retryAt); setNow(Date.now()); }
    } finally {
      if (operation.current === id) {
        pending.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };

  const verify = async (event) => {
    event.preventDefault();
    if (pending.current || !challenge || !/^\d{6}$/.test(code)) return;
    if (Date.parse(challenge.expiresAt) <= Date.now()) { setError('email_code_expired'); return; }
    const id = ++operation.current;
    const uid = currentUid();
    const generation = getEmailSignInGeneration();
    const isCurrent = () => mounted.current && operation.current === id;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const signInLink = await verifyEmailSignInCode(challenge.challengeId, code);
      if (!isCurrent() || currentUid() !== uid || getEmailSignInGeneration() !== generation) return;
      // The credential exists only in this invocation. Firebase consumes it in
      // the original tab; it is never stored, rendered, logged or navigated to.
      await auth.completeEmailCodeSignIn({ email: challenge.email, signInLink, isCurrent });
      clearPendingEmailSignIn(challenge.challengeId);
      if (isCurrent()) { setCode(''); setChallenge(null); }
    } catch (failure) {
      if (!isCurrent()) return;
      setError(getEmailSignInCodeError(failure));
      setCode('');
      if (failure.retryAt) { setRetryAt(failure.retryAt); setNow(Date.now()); }
    } finally {
      if (operation.current === id) {
        pending.current = false;
        if (mounted.current) setBusy(false);
      }
    }
  };

  const changeEmail = () => {
    if (pending.current) return;
    ++operation.current;
    clearPendingEmailSignIn(challenge?.challengeId);
    setChallenge(null);
    setCode('');
    setError('');
    setRetryAt(0);
  };
  return { email, setEmail, challenge, code, setCode, error, setError, busy, resendIn, expiresIn, send, verify, changeEmail };
}
